import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ENDPOINTS } from "@/api/application-api";
import { handleResponse } from "@/utils/errorResponseHandlers";
import { jsonAuthHeaders } from "@/utils/headers";

export interface McpSecretReference {
  env_var: string;
}

export interface McpServerConfig {
  type: "mcp";
  server_url: string;
  server_label?: string;
  headers?: Record<string, McpSecretReference>;
  allowed_tools?: string[];
  require_approval?: "never";
}

export interface McpServer {
  id: number;
  workspace_id: number;
  name: string;
  description: string | null;
  config: McpServerConfig;
  created_at: string;
  updated_at: string;
}

export interface McpServerInput {
  name: string;
  description?: string | null;
  config: McpServerConfig;
}

export interface DiscoveredMcpTool {
  name: string;
  description: string;
  input_schema: Record<string, unknown>;
}

const queryKey = (workspaceId: number) => ["mcp-servers", workspaceId] as const;

export const useMcpServers = ({
  workspaceId,
  userToken,
  search = "",
}: {
  workspaceId: number;
  userToken: string;
  search?: string;
}) =>
  useQuery({
    queryKey: [...queryKey(workspaceId), search],
    enabled: workspaceId > 0 && Boolean(userToken),
    queryFn: async (): Promise<McpServer[]> => {
      const url = new URL(ENDPOINTS.mcp_servers.list(workspaceId));
      if (search.trim()) {
        url.searchParams.set("search", search.trim());
      }
      const response = await fetch(url, { headers: jsonAuthHeaders(userToken) });
      const data = await handleResponse(response);
      return data.mcp_servers;
    },
  });

export const useCreateMcpServer = (workspaceId: number, userToken: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: McpServerInput): Promise<McpServer> => {
      const response = await fetch(ENDPOINTS.mcp_servers.list(workspaceId), {
        method: "POST",
        headers: jsonAuthHeaders(userToken),
        body: JSON.stringify(input),
      });
      const data = await handleResponse(response);
      return data.mcp_server;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKey(workspaceId) }),
  });
};

export const useUpdateMcpServer = (workspaceId: number, userToken: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      input,
    }: {
      id: number;
      input: McpServerInput;
    }): Promise<McpServer> => {
      const response = await fetch(ENDPOINTS.mcp_servers.get(workspaceId, id), {
        method: "PATCH",
        headers: jsonAuthHeaders(userToken),
        body: JSON.stringify(input),
      });
      const data = await handleResponse(response);
      return data.mcp_server;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKey(workspaceId) }),
  });
};

export const useDeleteMcpServer = (workspaceId: number, userToken: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const response = await fetch(ENDPOINTS.mcp_servers.get(workspaceId, id), {
        method: "DELETE",
        headers: jsonAuthHeaders(userToken),
      });
      return handleResponse(response);
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKey(workspaceId) }),
  });
};

export const useTestMcpServer = (workspaceId: number, userToken: string) =>
  useMutation({
    mutationFn: async (config: McpServerConfig): Promise<DiscoveredMcpTool[]> => {
      const response = await fetch(ENDPOINTS.mcp_servers.test(workspaceId), {
        method: "POST",
        headers: jsonAuthHeaders(userToken),
        body: JSON.stringify({ config }),
      });
      const data = await handleResponse(response);
      return data.tools;
    },
  });
